import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layouts/PageLayout';
import {
  CriticalResultsAlertsListPanel,
  CriticalResultsCriticalityPanel,
  CriticalResultsFilterBar,
  CriticalResultsKpiGrid,
  CriticalResultsRefreshBanner,
  CriticalResultsTrendChart,
  CriticalNotificationDetailModal,
} from '../../components/criticalResultsDashboard';
import { authService } from '../../auth/authService';
import {
  CRITICAL_RESULTS_KPI_DRILL_DOWN_PATH,
  CRITICAL_RESULTS_DEFAULT_REFRESH_INTERVAL_SEC,
  CRITICAL_RESULTS_LIST_PAGE_SIZE,
  CRITICAL_RESULTS_SUMMARY_EXPORT_DATASET,
} from '../../constants/criticalResultsDashboard.constants';
import {
  useCriticalResultsFilters,
  useCriticalResultsListParams,
  type CriticalResultsFilterState,
} from '../../hooks/useCriticalResultsFilters';
import {
  aggregateRefreshWarning,
  buildDepartmentOptions,
  buildReferringClinicianOptions,
  useCriticalResultsAutoRefresh,
  useCriticalResultsDefaultFacilityId,
  useCriticalResultsFacilities,
  useCriticalResultsNotifyWarning,
  useCriticalResultsSection,
  useCriticalResultsSuperAdmin,
} from '../../hooks/useCriticalResultsPageSetup';
import { useCriticalResultsExport } from '../../hooks/useCriticalResultsExport';
import {
  getCriticalResultsEscalationSummary,
  getCriticalResultsSummary,
  listCriticalResultsAlerts,
} from '../../services/criticalResultsDashboardService';
import type {
  CriticalResultsDashboardAlert,
  CriticalResultsDashboardSummary,
  CriticalResultsEscalationSummary,
  CriticalResultsExportFormat,
  CriticalResultsKpiKey,
} from '../../types/criticalResultsDashboard.types';
import type { CriticalNotificationDetailContext } from '../../types/criticalNotification.types';
import { buildDetailContextFromAlert } from '../../utils/criticalNotificationUtils';
import { mergeLastUpdated } from '../../utils/criticalResultsRefreshUtils';
import { buildCriticalResultsKpiItems } from '../../utils/criticalResultsKpiUtils';
import { formatLastRefreshedShort } from '../../utils/criticalResultsDashboardUtils';
import { canAccessCriticalResultsDashboard } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import './CriticalResultsDashboard.css';

const BREADCRUMBS = [
  { label: 'Home', path: '/' },
  { label: 'Dashboard', path: '/admin-dashboard' },
  { label: 'Analytics' },
  { label: 'Critical results' },
];

interface AlertsRefreshPayload {
  rows: CriticalResultsDashboardAlert[];
  count: number;
}

export default function CriticalResultsDashboard() {
  const navigate = useNavigate();
  const user = authService.getUser();
  const isSuperAdmin = useCriticalResultsSuperAdmin();
  const defaultFacilityId = useCriticalResultsDefaultFacilityId();
  const { notifyWarning, scheduleRetryRef, uiNotificationService } =
    useCriticalResultsNotifyWarning();
  const [detailContext, setDetailContext] = useState<CriticalNotificationDetailContext | null>(
    null
  );
  const [detailOpen, setDetailOpen] = useState(false);

  const { filters, apiFilters, updateFilters, clearFilters, buildDrillDownSearch } =
    useCriticalResultsFilters(defaultFacilityId);

  const { listState, updateListState, pageSize } = useCriticalResultsListParams({
    sortBy: 'startedAt',
    sortOrder: 'DESC',
  });

  const listQueryKey = useMemo(
    () =>
      JSON.stringify({
        page: listState.page,
        sortBy: listState.sortBy,
        sortOrder: listState.sortOrder,
      }),
    [listState.page, listState.sortBy, listState.sortOrder]
  );

  const {
    exporting,
    exportMenuOpen,
    toggleExportMenu,
    handleExport: runExport,
  } = useCriticalResultsExport(uiNotificationService);

  const updateDataFilters = useCallback(
    (partial: Partial<CriticalResultsFilterState>) => {
      updateFilters(partial, { resetPage: true });
    },
    [updateFilters]
  );

  const { facilitySelectOptions } = useCriticalResultsFacilities(
    isSuperAdmin,
    filters.facilityId,
    updateDataFilters
  );

  const summarySection = useCriticalResultsSection<CriticalResultsDashboardSummary>({
    enabled: Boolean(apiFilters),
    notifyWarning,
    fetcher: async () => {
      if (!apiFilters) {
        return { success: false, error: 'Missing facility filters' };
      }
      const result = await getCriticalResultsSummary(apiFilters);
      return result.success && result.data
        ? { success: true, data: result.data }
        : { success: false, error: result.error || 'Failed to load summary' };
    },
  });

  const escalationSection = useCriticalResultsSection<CriticalResultsEscalationSummary>({
    enabled: Boolean(apiFilters),
    notifyWarning,
    fetcher: async () => {
      if (!apiFilters) {
        return { success: false, error: 'Missing facility filters' };
      }
      const result = await getCriticalResultsEscalationSummary(apiFilters);
      return result.success && result.data
        ? { success: true, data: result.data }
        : { success: false, error: result.error || 'Failed to load chart data' };
    },
  });

  const alertsSection = useCriticalResultsSection<AlertsRefreshPayload>({
    enabled: Boolean(apiFilters),
    notifyWarning,
    fetcher: async () => {
      if (!apiFilters) {
        return { success: false, error: 'Missing facility filters' };
      }
      const result = await listCriticalResultsAlerts('all', {
        ...apiFilters,
        limit: CRITICAL_RESULTS_LIST_PAGE_SIZE,
        offset: (listState.page - 1) * CRITICAL_RESULTS_LIST_PAGE_SIZE,
        sortBy: listState.sortBy,
        sortOrder: listState.sortOrder,
      });
      return result.success
        ? { success: true, data: { rows: result.data, count: result.count } }
        : { success: false, error: result.error || 'Failed to load critical alerts' };
    },
  });

  const summary = summarySection.data;
  const escalationSummary = escalationSection.data;
  const alerts = alertsSection.data?.rows ?? [];
  const alertCount = alertsSection.data?.count ?? 0;
  const apiFiltersKey = useMemo(() => JSON.stringify(apiFilters), [apiFilters]);

  useCriticalResultsAutoRefresh(
    filters.autoRefresh && Boolean(apiFilters),
    CRITICAL_RESULTS_DEFAULT_REFRESH_INTERVAL_SEC,
    trigger => {
      void summarySection.refresh(trigger);
      void escalationSection.refresh(trigger);
      void alertsSection.refresh(trigger);
    },
    scheduleRetryRef
  );

  useEffect(() => {
    if (!apiFilters) {
      return;
    }
    void summarySection.refresh('initial');
    void escalationSection.refresh('initial');
    void alertsSection.refresh('initial');
  }, [apiFiltersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!apiFilters) {
      return;
    }
    void alertsSection.refresh('initial');
  }, [listQueryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualRefresh = useCallback(() => {
    void summarySection.refresh('manual');
    void escalationSection.refresh('manual');
    void alertsSection.refresh('manual');
  }, [summarySection, escalationSection, alertsSection]);

  const handleAlertSelect = useCallback((alert: CriticalResultsDashboardAlert) => {
    setDetailContext(buildDetailContextFromAlert(alert));
    setDetailOpen(true);
  }, []);

  const handleDetailClose = useCallback(() => {
    setDetailOpen(false);
    setDetailContext(null);
  }, []);

  const handleAcknowledged = useCallback(() => {
    void summarySection.refresh('manual');
    void escalationSection.refresh('manual');
    void alertsSection.refresh('manual');
  }, [summarySection, escalationSection, alertsSection]);

  const kpiItems = useMemo(() => buildCriticalResultsKpiItems(summary), [summary]);
  const departmentOptions = useMemo(
    () => buildDepartmentOptions(summary?.departmentSummary),
    [summary?.departmentSummary]
  );

  const referringClinicianOptions = useMemo(
    () =>
      buildReferringClinicianOptions(
        summary?.referringClinicianSummary,
        filters.referringClinician
      ),
    [summary?.referringClinicianSummary, filters.referringClinician]
  );

  const lastUpdated = useMemo(
    () =>
      mergeLastUpdated([
        summarySection.lastUpdated,
        escalationSection.lastUpdated,
        alertsSection.lastUpdated,
      ]),
    [summarySection.lastUpdated, escalationSection.lastUpdated, alertsSection.lastUpdated]
  );

  const refreshWarning = useMemo(
    () =>
      aggregateRefreshWarning(
        [summarySection, escalationSection, alertsSection],
        'Some dashboard sections could not be refreshed. Existing data is shown.'
      ),
    [summarySection, escalationSection, alertsSection]
  );

  const initialLoading = summarySection.initialLoading || escalationSection.initialLoading;
  const initialLoadFailed =
    !initialLoading &&
    !summary &&
    Boolean(summarySection.warning) &&
    Boolean(escalationSection.warning);
  const refreshing =
    summarySection.refreshing || escalationSection.refreshing || alertsSection.refreshing;
  const alertsLoading = alertsSection.initialLoading;

  const handleKpiSelect = (key: CriticalResultsKpiKey) => {
    const path = CRITICAL_RESULTS_KPI_DRILL_DOWN_PATH[key];
    if (!path) {
      return;
    }
    navigate(`${path}${buildDrillDownSearch()}`);
  };

  const onExport = (format: CriticalResultsExportFormat) => {
    if (!apiFilters) {
      return;
    }
    runExport(format, CRITICAL_RESULTS_SUMMARY_EXPORT_DATASET, apiFilters);
  };

  if (!canAccessCriticalResultsDashboard(user)) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  if (!defaultFacilityId && !isSuperAdmin) {
    return (
      <PageLayout
        title="Critical Results"
        breadcrumbs={BREADCRUMBS}
      >
        <p className="crd-message">Your account is not associated with a facility.</p>
      </PageLayout>
    );
  }

  if (!filters.facilityId) {
    return (
      <PageLayout
        title="Critical Results"
        breadcrumbs={BREADCRUMBS}
      >
        <p className="crd-message">
          {isSuperAdmin
            ? 'Select a facility to view the critical results dashboard.'
            : 'Your account is not associated with a facility.'}
        </p>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Critical Results"
      hideTitleInHeader
      breadcrumbs={BREADCRUMBS}
    >
      <div className="crd-dashboard">
        <CriticalResultsFilterBar
          startDate={filters.startDate}
          endDate={filters.endDate}
          onStartDateChange={value => updateDataFilters({ startDate: value })}
          onEndDateChange={value => updateDataFilters({ endDate: value })}
          facilityOptions={facilitySelectOptions}
          facilityId={filters.facilityId}
          onFacilityChange={value => updateDataFilters({ facilityId: value })}
          showFacilityFilter={isSuperAdmin}
          department={filters.department}
          departmentOptions={departmentOptions}
          onDepartmentChange={value => updateDataFilters({ department: value })}
          criticalityBand={filters.criticalityBand}
          onCriticalityBandChange={value => updateDataFilters({ criticalityBand: value })}
          status={filters.status}
          onStatusChange={value => updateDataFilters({ status: value })}
          escalationLevel={filters.escalationLevel}
          onEscalationLevelChange={value => updateDataFilters({ escalationLevel: value })}
          referringClinician={filters.referringClinician}
          referringClinicianOptions={referringClinicianOptions}
          onReferringClinicianChange={value => updateDataFilters({ referringClinician: value })}
          autoRefresh={filters.autoRefresh}
          onAutoRefreshChange={value => updateFilters({ autoRefresh: value })}
          lastRefreshedLabel={formatLastRefreshedShort(lastUpdated)}
          refreshIntervalSec={CRITICAL_RESULTS_DEFAULT_REFRESH_INTERVAL_SEC}
          loading={initialLoading}
          refreshing={refreshing}
          exporting={exporting}
          exportMenuOpen={exportMenuOpen}
          onExportMenuToggle={toggleExportMenu}
          onExport={onExport}
          onClear={clearFilters}
          onRefresh={handleManualRefresh}
        />

        <CriticalResultsRefreshBanner
          message={refreshWarning}
          onRetry={handleManualRefresh}
        />

        {initialLoadFailed ? (
          <p className="crd-error crd-error--page">
            Unable to load dashboard data. Check your connection and try again.
          </p>
        ) : null}

        <CriticalResultsKpiGrid
          items={kpiItems}
          loading={initialLoading}
          onSelect={handleKpiSelect}
        />

        <div
          className={['crd-analytics-row', initialLoading ? 'crd-analytics-row--loading' : '']
            .filter(Boolean)
            .join(' ')}
        >
          <CriticalResultsTrendChart buckets={escalationSummary?.timeSeries || []} />
          <CriticalResultsCriticalityPanel items={summary?.criticalitySummary || []} />
        </div>

        <CriticalResultsAlertsListPanel
          variant="overview"
          title="Critical alerts"
          alerts={alerts}
          totalCount={alertCount}
          currentPage={listState.page}
          pageSize={pageSize}
          loading={alertsLoading}
          refreshing={alertsSection.refreshing}
          error={alertsSection.warning}
          search=""
          sortBy={listState.sortBy}
          sortOrder={listState.sortOrder}
          onSearchChange={() => undefined}
          onSortChange={(sortBy, sortOrder) => updateListState({ sortBy, sortOrder, page: 1 })}
          onPageChange={page => updateListState({ page })}
          onAlertSelect={handleAlertSelect}
        />

        <CriticalNotificationDetailModal
          open={detailOpen}
          context={detailContext}
          onClose={handleDetailClose}
          onAcknowledged={handleAcknowledged}
        />
      </div>
    </PageLayout>
  );
}
